/**
 * Worksheet event listeners, so the taskpane can refresh when a sheet is deleted or renamed.
 *
 * The handles live here as module state rather than in React state for two reasons: they are not
 * render input, and removing one requires re-entering the very `Excel.run` context it was created
 * in — `Excel.run(handle.context, …)`. Holding them in a component's state made that context
 * reachable only through a closure, so two registrations in the same tick could leak the first pair.
 */
let deleteHandle: OfficeExtension.EventHandlerResult<Excel.WorksheetDeletedEventArgs> | null = null;
let nameChangeHandle: OfficeExtension.EventHandlerResult<Excel.WorksheetNameChangedEventArgs> | null = null;

let queue: Promise<unknown> = Promise.resolve();

/**
 * Runs `work` only once every earlier registration change has settled.
 *
 * Both operations assign the handles across an `await`, so overlapping callers — a Strict Mode
 * remount, an effect cleanup racing the next effect — would otherwise each read the handles before
 * the other had written them, and the earlier registration would stay attached with nothing left
 * pointing at it.
 */
const serialize = <T>(work: () => Promise<T>): Promise<T> => {
  const result = queue.then(work, work);
  queue = result.catch(() => undefined);
  return result;
};

const detach = async (): Promise<void> => {
  if (!deleteHandle && !nameChangeHandle) return;

  // Cleared before awaiting: a failure to detach must not leave a stale handle that a later call
  // would try to remove a second time.
  const [toDelete, toRename] = [deleteHandle, nameChangeHandle];
  deleteHandle = null;
  nameChangeHandle = null;

  // One try per handle, not one around both: they are removed in separate batches, so sharing a
  // catch would let a failure on the first skip the second — and with the module state already
  // cleared, nothing would be left that could detach it.
  await remove(toDelete);
  await remove(toRename);
};

const remove = async (handle: OfficeExtension.EventHandlerResult<unknown> | null): Promise<void> => {
  if (!handle) return;
  try {
    await Excel.run(handle.context, async (context) => {
      handle.remove();
      await context.sync();
    });
  } catch (error) {
    console.error("Error unregistering worksheet listener:", error);
  }
};

/** Replaces any existing registration, so calling this twice cannot leave a listener behind. */
export const registerWorksheetListeners = (onUpdate: () => Promise<void>): Promise<void> =>
  serialize(async () => {
    await detach();

    await Excel.run(async (context) => {
      const worksheets = context.workbook.worksheets;
      deleteHandle = worksheets.onDeleted.add(onUpdate);
      nameChangeHandle = worksheets.onNameChanged.add(onUpdate);
      await context.sync();
    });
  });

export const unregisterWorksheetListeners = (): Promise<void> => serialize(detach);
